<script lang="ts" setup>
import {Head, useForm} from "@inertiajs/vue3";
import BaseLayout from "@/layouts/BaseLayout.vue";
import TextField from "@/components/forms/TextField.vue";
import RadioList from "@/components/forms/RadioList.vue";
import TextArea from "@/components/forms/TextArea.vue";

defineOptions({layout: BaseLayout})

const form = useForm({
    name: '',
    slug: '',
    description: '',
    default_login: '',
    host: '',
    port: 22,
    is_public: true,
    has_groups: false,
    gpg_public_key: '',
    upload_path: '',
})

const items = [
    {
        value: true,
        label: 'Public',
        description: 'Anyone can access this project',
        icon: 'mdi-web'
    },
    {
        value: false,
        label: 'Private',
        description: 'You choose who can access this project',
        icon: 'mdi-lock-outline'
    }
]

const submit = () => {
    form.post(route('projects.store'))
}
</script>

<template>
    <Head title="Create project" />
    <v-row
        :no-gutters="true"
        justify="center"
        class="mt-15"
    >
        <v-col
            cols="10"
            md="6"
            xl="5"
        >
            <v-card :flat="true">
                <v-form @submit.prevent="submit">
                    <v-card-subtitle class="text-h6 text-center">
                        SendCrypt
                    </v-card-subtitle>
                    <v-card-title class="text-h4 text-center font-weight-bold mb-15">
                        Create a new project
                    </v-card-title>
                    <v-card-text class="pt-1">
                        <v-row class="mt-3">
                            <v-col
                                cols="12"
                                class="py-0"
                            >
                                <text-field
                                    v-model="form.name"
                                    :errors="form.errors.name || form.errors.slug"
                                    label="Name"
                                    :required="true"
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col
                                cols="12"
                                class="py-0"
                            >
                                <text-field
                                    v-model="form.description"
                                    :errors="form.errors.description"
                                    label="Description"
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col
                                cols="12"
                                class="py-0"
                            >
                                <radio-list
                                    v-model="form.is_public"
                                    :items="items"
                                    @change="$event ? form.reset('has_groups') : null"
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col
                                cols="12"
                                class="py-0 mt-3"
                            >
                                <label
                                    v-if="!form.is_public"
                                    class="font-weight-bold black--text"
                                >Groups</label>
                                <v-checkbox
                                    v-if="!form.is_public"
                                    v-model="form.has_groups"
                                    class="mt-0 py-0"
                                    color="black"
                                    :error-messages="form.errors.has_groups"
                                    density="compact"
                                    rounded="lg"
                                >
                                    <template #label>
                                        <v-list-item
                                            density="compact"
                                        >
                                            <v-list-item-title class="text-body-2 font-weight-bold">
                                                Activate user groups
                                            </v-list-item-title>
                                            <v-list-item-subtitle class="text-caption font-weight-regular">
                                                This option allows multiple SSH usernames. <a class="text-primary">Learn more
                                                    about groups.</a>
                                            </v-list-item-subtitle>
                                        </v-list-item>
                                    </template>
                                </v-checkbox>
                            </v-col>
                        </v-row>
                        <v-row class="mt-3">
                            <v-col
                                cols="8"
                                class="py-0"
                            >
                                <text-field
                                    v-model="form.host"
                                    :errors="form.errors.host"
                                    label="Host"
                                    :required="true"
                                />
                            </v-col>
                            <v-col
                                cols="4"
                                class="py-0"
                            >
                                <text-field
                                    v-model="form.port"
                                    :errors="form.errors.port"
                                    label="Port"
                                    type="number"
                                    :required="true"
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col
                                cols="12"
                                class="py-0"
                            >
                                <text-field
                                    v-model="form.default_login"
                                    :errors="form.errors.default_login"
                                    label="Username (default login)"
                                    :required="!form.has_groups"
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col
                                cols="12"
                                class="py-0"
                            >
                                <text-field
                                    v-model="form.upload_path"
                                    :errors="form.errors.upload_path"
                                    label="Remote path"
                                />
                            </v-col>
                        </v-row>
                        <v-row class="mt-3">
                            <v-col
                                cols="12"
                                class="py-0"
                            >
                                <text-area
                                    v-model="form.gpg_public_key"
                                    :errors="form.errors.gpg_public_key"
                                    label="PGP public key"
                                    :required="true"
                                />
                            </v-col>
                        </v-row>
                        <p class="mt-3">
                            <v-icon left>
                                mdi-alert-circle-outline
                            </v-icon>
                            Once the PGP public key is set, it can no longer be changed.
                        </p>
                    </v-card-text>
                    <v-card-actions class="px-4">
                        <v-spacer />
                        <submit-btn
                            :loading="form.processing"
                            type="submit"
                            :block="true"
                            size="large"
                        >
                            <span class="font-weight-bold">Create project</span>
                        </submit-btn>
                    </v-card-actions>
                </v-form>
            </v-card>
        </v-col>
    </v-row>
</template>

<style scoped>
</style>
